import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useTheme } from '../contexts/ThemeContext'

interface Notification {
  id: string
  type: 'mention' | 'reply' | 'like' | 'comment'
  message: string
  is_read: boolean
  created_at: string
  video_id?: string
  comment_id?: string
  from_user?: { username: string }
}

const fmtTimeAgo = (d: string) => {
  const diff = Date.now() - new Date(d).getTime()
  const s = Math.floor(diff / 1000), m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000), day = Math.floor(diff / 86400000)
  if (s < 10) return 'Just now'
  if (s < 60) return `${s}s ago`
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  return `${day}d ago`
}

const TYPE_ICON: Record<string, string> = {
  mention: '💬', reply: '↩', like: '❤️', comment: '🗨'
}

interface Props { userId: string }

export default function NotificationsBell({ userId }: Props) {
  const { isDark } = useTheme()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unread, setUnread] = useState(0)
  const panelRef = useRef<HTMLDivElement>(null)

  const C = {
    panel: isDark ? '#0f1018' : '#fff',
    border: isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)',
    text: isDark ? '#fff' : '#0f172a',
    sub: isDark ? '#555' : '#94a3b8',
    hover: isDark ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.04)',
    unreadBg: isDark ? 'rgba(229,57,53,.08)' : 'rgba(229,57,53,.05)',
  }

  useEffect(() => {
    fetchNotifications()
    // Real-time subscription
    const sub = supabase
      .channel('notifications:' + userId)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, () => fetchNotifications())
      .subscribe()
    return () => { supabase.removeChannel(sub) }
  }, [userId])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const fetchNotifications = async () => {
    const { data } = await supabase
      .from('notifications')
      .select('*, from_user:profiles!from_user_id(username)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(30)
    if (data) {
      setNotifications(data)
      setUnread(data.filter(n => !n.is_read).length)
    }
  }

  const markAllRead = async () => {
    await supabase.rpc('mark_notifications_read', { p_user_id: userId })
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    setUnread(0)
  }

  const handleClick = async (n: Notification) => {
    // Mark as read
    await supabase.from('notifications').update({ is_read: true }).eq('id', n.id)
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x))
    setUnread(prev => Math.max(0, prev - 1))
    setOpen(false)
    if (n.video_id) navigate(`/video/${n.video_id}`)
  }

  const deleteNotification = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    await supabase.from('notifications').delete().eq('id', id)
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== id)
      setUnread(updated.filter(n => !n.is_read).length)
      return updated
    })
  }

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => { setOpen(v => !v); if (!open && unread > 0) markAllRead() }}
        style={{
          position: 'relative', width: 40, height: 40, borderRadius: 20,
          background: open
            ? (isDark ? 'rgba(229,57,53,.15)' : 'rgba(229,57,53,.1)')
            : (isDark ? 'rgba(255,255,255,.06)' : 'rgba(0,0,0,.05)'),
          border: `1px solid ${open ? 'rgba(229,57,53,.4)' : C.border}`,
          cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 17, transition: 'all .2s',
        }}
      >
        🔔
        {unread > 0 && (
          <div style={{
            position: 'absolute', top: -3, right: -3,
            background: '#e53935', borderRadius: 10,
            minWidth: 18, height: 18, padding: '0 4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${isDark ? '#080810' : '#f0f2f8'}`,
          }}>
            <span style={{ color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: 1 }}>
              {unread > 99 ? '99+' : unread}
            </span>
          </div>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 10px)', right: 0,
          width: 360, maxHeight: 480,
          background: C.panel,
          border: `1px solid ${C.border}`,
          borderRadius: 18, overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,.4)',
          zIndex: 200,
          animation: 'notifIn .2s ease',
        }}>
          <style>{`@keyframes notifIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>

          {/* Header */}
          <div style={{
            padding: '14px 18px',
            borderBottom: `1px solid ${C.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div>
              <span style={{ color: C.text, fontSize: 15, fontWeight: 800 }}>Notifications</span>
              {unread > 0 && (
                <span style={{
                  marginLeft: 8, padding: '2px 8px', borderRadius: 10,
                  background: 'rgba(229,57,53,.15)', color: '#e53935',
                  fontSize: 11, fontWeight: 700,
                }}>
                  {unread} new
                </span>
              )}
            </div>
            {notifications.length > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none', border: 'none', color: '#e53935',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', maxHeight: 400 }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 10, opacity: .3 }}>🔔</div>
                <p style={{ color: C.sub, fontSize: 14 }}>No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '12px 16px', cursor: 'pointer',
                    background: n.is_read ? 'transparent' : C.unreadBg,
                    borderBottom: `1px solid ${C.border}`,
                    transition: 'background .15s', position: 'relative',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.hover }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? 'transparent' : C.unreadBg }}
                >
                  {/* Unread dot */}
                  {!n.is_read && (
                    <div style={{
                      position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                      width: 6, height: 6, borderRadius: 3, background: '#e53935',
                    }} />
                  )}

                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 18, flexShrink: 0,
                    background: n.type === 'mention'
                      ? 'linear-gradient(135deg,#e53935,#8e24aa)'
                      : n.type === 'like'
                      ? 'linear-gradient(135deg,#f97316,#ef4444)'
                      : 'linear-gradient(135deg,#3b82f6,#06b6d4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16,
                  }}>
                    {TYPE_ICON[n.type] || '🔔'}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      color: C.text, fontSize: 13, fontWeight: n.is_read ? 400 : 600,
                      margin: '0 0 3px', lineHeight: 1.4,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {n.message}
                    </p>
                    <span style={{ color: C.sub, fontSize: 11 }}>{fmtTimeAgo(n.created_at)}</span>
                  </div>

                  {/* Delete */}
                  <button
                    onClick={e => deleteNotification(e, n.id)}
                    style={{
                      background: 'none', border: 'none', color: C.sub,
                      fontSize: 14, cursor: 'pointer', padding: '0 4px',
                      flexShrink: 0, opacity: .6, transition: 'opacity .15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
                  >
                    ✕
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}